import { singularSdk, SingularConfig } from 'singular-sdk';

import { singularSdkKey, singularSdkSecret } from '@/env';
import { logger } from '@/lib/logger';
import { getDeviceSetting, setDeviceSetting } from '@/utils/localStorage';

const PRODUCT_ID = 'com.genos.littlebird';
const INSTALLED_FLAG = 'singular_desktop_installed';

let isInitialized = false;

export function isSingularEnabled(): boolean {
  return Boolean(singularSdkKey && singularSdkSecret);
}

/**
 * Initialize the Singular web SDK so session attribution flows to the
 * Singular dashboard. Safe to call multiple times — only the first call runs.
 */
export function initSingular(): void {
  if (isInitialized) return;

  if (!isSingularEnabled()) {
    logger.warn(
      'Singular SDK key or secret missing; attribution will not be captured'
    );
    return;
  }

  try {
    singularSdk.init(
      new SingularConfig(singularSdkKey!, singularSdkSecret!, PRODUCT_ID)
    );
    isInitialized = true;
  } catch (err) {
    logger.warn('Singular init failed; attribution will not be captured', {
      error: String(err),
    });
  }
}

/**
 * Associate the current Singular session with a stable internal user ID so
 * attribution persists across sessions. Pass an opaque internal ID, not PII.
 */
export function setSingularUser(userId: string): void {
  if (!isInitialized) return;
  try {
    singularSdk.login(userId);
  } catch (err) {
    logger.warn('Singular login failed', { error: String(err) });
  }
}

/**
 * Clear the Singular user identifier on logout so the next session is
 * anonymous until another user signs in.
 */
export function clearSingularUser(): void {
  if (!isInitialized) return;
  try {
    singularSdk.logout();
  } catch (err) {
    logger.warn('Singular logout failed', { error: String(err) });
  }
}

/**
 * Fire a one-time Desktop App Installed conversion event for attribution.
 * Uses a device-setting flag (survives logout, cleared only on app reinstall)
 * so the event fires exactly once per installation.
 * Must be called after setSingularUser so the event is attributed correctly.
 */
export function trackDesktopAppInstalled(): void {
  if (!isInitialized) return;
  if (getDeviceSetting(INSTALLED_FLAG)) return;

  try {
    singularSdk.conversionEvent('desktop_app_installed');
    setDeviceSetting(INSTALLED_FLAG, 'true');
  } catch (err) {
    logger.warn('Singular desktop_app_installed event failed', {
      error: String(err),
    });
  }
}
